/**
 * `useReportOffSick` — the lead's "Report off sick" write from the roster
 * grid (KKH-REQ-05, hardening §9.3).
 *
 * A deployer taking an MC by phone call had to leave the roster, open the
 * Team page's generic date-request form, find the person and paint a day.
 * This is the same write from where they are standing: ONE date, ONE
 * person, the existing lead on-behalf route
 * (`POST /api/teams/{team_id}/date-requests`, `request_type: "mc"`).
 *
 * No new endpoint and no second lifecycle: the row lands `indicated`
 * exactly as a member's phone report does, so the seat reads *off sick*
 * and the day counts short immediately.
 *
 * The invalidations are the point of the hook — every surface that shows
 * the consequence has to move together:
 *   * the cycle roster (the struck seat + the *off sick* tag);
 *   * the day's shortfall options (the ladder now has a cell to offer);
 *   * the leave review queue + the team date-request windows (the row);
 *   * the mc-attention feed (the "Review cover · N" chip).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { type ApiError, apiFetch } from "./client";
import { coverageReviewQueryFilter } from "./useCoverageReview";
import { cycleRosterQueryFilter } from "./useCycleRoster";
import type { DateRequestRead } from "./useTeamDateRequests";

export interface ReportOffSickVars {
  /** The absent person's MEMBERSHIP id (`RosterMember.id` on the grid). */
  membershipId: string;
  /** The single day, ISO `YYYY-MM-DD`. */
  date: string;
}

export function useReportOffSick(teamId: string | undefined, cycleId: string) {
  const qc = useQueryClient();
  return useMutation<DateRequestRead, ApiError | Error, ReportOffSickVars>({
    mutationFn: ({ membershipId, date }) =>
      apiFetch<DateRequestRead>(`/api/teams/${teamId}/date-requests`, {
        method: "POST",
        body: JSON.stringify({
          membership_id: membershipId,
          start_date: date,
          end_date: date,
          request_type: "mc",
        }),
      }),
    onSuccess: () => {
      // Both roster variants (lead + member), whichever account cached them.
      qc.invalidateQueries(cycleRosterQueryFilter(cycleId));
      qc.invalidateQueries({ queryKey: ["cycle-shortfall-options", cycleId] });
      qc.invalidateQueries({ queryKey: ["leave-review", cycleId] });
      qc.invalidateQueries({ queryKey: ["team-date-requests"] });
      // #2277 — an MC counts toward the daily leave-quota lines.
      qc.invalidateQueries({ queryKey: ["leave-quota"] });
      qc.invalidateQueries({ queryKey: ["mc-attention", cycleId] });
      // Backlog #91 — the "Coverage · N short" chip AND the coverage side
      // panel read `/coverage-review`. Both were left on the pre-report
      // count: the chip said "2 short" while the grid under it said −4 and
      // the panel listed 4 gaps. By PREDICATE — this hook has no accountId,
      // and the real key carries one.
      qc.invalidateQueries(coverageReviewQueryFilter(cycleId));
      // An absence on a day the person holds a fixed placement opens an
      // allocation gap; the gap list + the grid's open-seat callout share
      // that read. Prefix-invalidated for the same accountId reason.
      qc.invalidateQueries({ queryKey: ["cycle-allocation-gaps", cycleId] });
    },
  });
}
